/* eslint-disable */
import React from "react";
import { Link, useLocation } from "react-router-dom";
import DashIcon from "components/icons/DashIcon";
import { FaCircle } from "react-icons/fa";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
} from "@chakra-ui/accordion";
import { useAuthContext } from "auth/AuthProvider";
import { useSidebarCountsContext } from "contexts/SidebarCountsContext";
import { PERMISSIONS } from "auth/permissions";
import { useTranslation } from "react-i18next";
// chakra imports

// Routes that require SUPER_ADMIN permission to access
const SUPER_ADMIN_ROUTES = new Set([
  "/subscriptions",
  "/subscription-plans",
  "/global-settings",
]);

export function SidebarLinks(props: {
  routes: RoutesType[];
  onClose?: () => void;
  [x: string]: any;
}) {
  //  Chakra color mode
  const { t } = useTranslation();
  let location = useLocation();
  const { hasFullControl, hasPermission } = useAuthContext();
  const { counts } = useSidebarCountsContext();

  const { routes, hovered, mini, onClose } = props;

  const getRouteName = (route: RoutesType): string => {
    if (route.nameKey && typeof route.nameKey === 'string') {
      return t(route.nameKey);
    }
    return route.name;
  };
  const activeRoute = (routeName: string) => {
    // Only take the second segment of the pathname (after the first '/')
    const segments = location.pathname.split("/");
    const secondPath = segments[2] || "";
    return secondPath === routeName.replace(/^\//, "").split("/")[0];
  };

  const getCountForRoute = (route: RoutesType): number => {
    const routePath = route.path.toLowerCase();
    if (routePath.includes('feedback')) {
      return counts.feedbackCount;
    }
    if (routePath.includes('technical-notes')) {
      return counts.technicalNotesCount;
    }
    return 0;
  };

  const createLinks = (routes: RoutesType[]) => {
    return routes.map((route, key) => {
      if (route.hidden) return null;
      
      // Hide management routes for users without FULL_CONTROL, only show Chat
      if (!hasFullControl() && route.path !== "/chat") {
        return null;
      }

      // Hide Super Admin routes for users without SUPER_ADMIN permission
      if (SUPER_ADMIN_ROUTES.has(route.path) && !hasPermission(PERMISSIONS.SUPER_ADMIN)) {
        return null;
      }

      if (route.collapse) {
        return (
          <Accordion
            defaultIndex={activeRoute(route.path.toLowerCase()) ? 0 : 1}
            allowToggle
            key={key}
          >
            <AccordionItem mb="8px" border="none" key={key}>
              <AccordionButton
                display="flex"
                _hover={{
                  bg: "unset",
                }}
                _focus={{
                  boxShadow: "none",
                }}
                borderRadius="8px"
                w={{
                  sm: "100%",
                  xl: "100%",
                }}
                px={route.icon ? null : "0px"}
                py="0px"
                bg={"transparent"}
                ms={0}
                mb="4px"
              >
                {route.icon ? (
                  <div
                    className={`mb-1.5 flex w-full items-center pl-8 pr-7 ${
                      mini === false
                        ? " justify-between"
                        : mini === true && hovered === true
                        ? " justify-between"
                        : " justify-center"
                    }`}
                  >
                    <div>
                      <div className="align-center flex w-full justify-center">
                        <div
                          className={`flex items-center justify-center ${
                            mini === false
                              ? "mr-3.5"
                              : mini === true && hovered
                              ? "mr-3.5"
                              : "mx-auto"
                          } ${
                            activeRoute(route.path.toLowerCase())
                              ? "text-brand-500 dark:text-white"
                              : "text-gray-600"
                          } ${
                            activeRoute(route.path.toLowerCase())
                              ? "22px"
                              : "26px"
                          }`}
                        >
                          {route.icon}
                        </div>
                        <p
                          className={`mr-auto
                          ${
                            mini === false
                              ? "block"
                              : mini === true && hovered === true
                              ? "block"
                              : "block xl:hidden"
                          } ${
                            activeRoute(route.path.toLowerCase())
                              ? "text-700 font-medium text-navy-700 dark:text-white"
                              : "font-medium text-gray-600"
                          } `}
                        >
                          {getRouteName(route)}
                        </p>
                      </div>
                    </div>
                    <AccordionIcon
                      ms="auto"
                      className={`!text-gray-600 
                          ${
                            mini === false
                              ? "block"
                              : mini === true && hovered === true
                              ? "block"
                              : "block xl:hidden"
                          }`}
                      display={
                        mini === false
                          ? "block"
                          : mini === true && hovered === true
                          ? "block"
                          : { base: "block", xl: "none" }
                      }
                      transform={route.icon ? null : "translateX(-70%)"}
                    />
                  </div>
                ) : (
                  <div
                    className={`flex w-full items-center pb-0 pt-0 ${
                      mini === false
                        ? "ml-5 pl-12"
                        : mini === true && hovered === true
                        ? "ml-5 pl-12"
                        : "ml-5 pl-12 xl:ml-[unset] xl:justify-center xl:pl-8 xl:pr-7 "
                    } pr-7`}
                  >
                    <div>
                      <p
                        className={`mr-auto text-sm font-medium ${
                          activeRoute(route.path.toLowerCase())
                            ? "text-800 text-navy-700 dark:text-white"
                            : "text-gray-600"
                        } ${
                          activeRoute(route.path.toLowerCase())
                            ? "22px"
                            : "26px"
                        }`}
                      >
                        {(() => {
                          const routeName = getRouteName(route);
                          return mini === false
                            ? routeName
                            : mini === true && hovered === true
                            ? routeName
                            : routeName[0];
                        })()}
                      </p>
                    </div>
                    <AccordionIcon
                      ms="auto"
                      className={`!text-gray-600 
                          ${
                            mini === false
                              ? "block"
                              : mini === true && hovered === true
                              ? "block"
                              : "block xl:hidden"
                          }`}
                      display={
                        mini === false
                          ? "block"
                          : mini === true && hovered === true
                          ? "block"
                          : "none"
                      }
                      transform={null}
                    />
                  </div>
                )}
              </AccordionButton>
              <AccordionPanel
                pe={route.icon ? null : "0px"}
                py="0px"
                ps={
                  route.icon
                    ? mini === false
                      ? "8px"
                      : mini === true && hovered === true
                      ? "8px"
                      : "base:8px xl:0px"
                    : mini === false
                    ? "8px"
                    : mini === true && hovered === true
                    ? "8px"
                    : "base:8px xl:0px"
                }
                display={
                  mini === false
                    ? "block"
                    : mini === true && hovered === true
                    ? "block"
                    : "base:block xl:flex"
                }
              >
                <ul>
                  {
                    route.icon
                      ? createLinks(route.items) // for bullet accordion links
                      : createAccordionLinks(route.items) // for non-bullet accordion links
                  }
                </ul>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        );
      } else {
        return (
          <Link to={route.layout + route.path} key={key} onClick={onClose}>
            {route.icon ? (
              <div className="relative mb-3 flex hover:cursor-pointer">
                <li className="my-[3px] flex cursor-pointer items-center px-[30px]">
                  <span
                    className={`${
                      activeRoute(route.path) === true
                        ? "font-bold text-brand-500 dark:text-white"
                        : "font-medium text-gray-600"
                    }`}
                  >
                    {route.icon ? route.icon : <DashIcon />}{" "}
                  </span>
                        <p
                          className={`leading-1 ml-4 ${
                            activeRoute(route.path) === true
                              ? "font-bold text-navy-700 dark:text-white"
                              : "font-medium text-gray-600"
                          }`}
                        >
                          {getRouteName(route)}
                        </p>
                </li>
                {getCountForRoute(route) > 0 && (
                  <span className="absolute top-0 right-16 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-500 rounded-full min-w-[18px] h-[18px]">
                    {getCountForRoute(route)}
                  </span>
                )}
                {activeRoute(route.path) ? (
                  <div className="absolute right-0 top-px h-9 w-1 rounded-lg bg-brand-500 dark:bg-brand-400" />
                ) : null}
              </div>
            ) : (
              <div
                className={`relative mb-2 flex ${
                  mini === false
                    ? ""
                    : mini === true && hovered === true
                    ? ""
                    : "xl:justify-center"
                } hover:cursor-pointer`}
              >
                <li
                  className="my-[3px] flex cursor-pointer items-center px-[22px]"
                  key={key}
                >
                  <span
                    className={`flex text-sm leading-none ${
                      mini === false
                        ? "ml-9"
                        : mini === true && hovered === true
                        ? "ml-9"
                        : "ml-9 xl:ml-0"
                    } ${
                      activeRoute(route.path) === true
                        ? "font-medium text-navy-700 dark:text-white"
                        : "font-medium text-gray-600"
                    }`}
                  >
                    {(() => {
                      const routeName = getRouteName(route);
                      return mini === false
                        ? routeName
                        : mini === true && hovered === true
                        ? routeName
                        : routeName[0];
                    })()}
                  </span>
                </li>
                {getCountForRoute(route) > 0 && (
                  <span className="absolute top-0 right-16 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-500 rounded-full min-w-[18px] h-[18px]">
                    {getCountForRoute(route)}
                  </span>
                )}
              </div>
            )}
          </Link>
        );
      }
    });
  };
  const createAccordionLinks = (routes: RoutesType[]) => {
    return routes.map((route, index) => {
      if (
        route.layout === "/admin" ||
        route.layout === "/auth" ||
        route.layout === "/rtl"
      ) {
        return (
          <Link key={index} to={route.layout + route.path} onClick={onClose}>
            <div
              className={`relative ${
                mini === false
                  ? "ml-7"
                  : mini === true && hovered === true
                  ? "ml-7"
                  : "ml-7 xl:ml-4"
              } mb-1 flex hover:cursor-pointer`}
            >
              <li
                className="my-[3px] flex cursor-pointer items-center px-8"
                key={index}
              >
                <span className={`text-brand-500 dark:text-white`}>
                  <FaCircle className={`mr-0.5 h-1.5 w-1.5`} />
                </span>
                <span
                  className={`ml-2 flex text-sm ${
                    activeRoute(route.path) === true
                      ? "font-medium text-navy-700 dark:text-white"
                      : "font-medium text-gray-600"
                  }`}
                >
                  {(() => {
                    const routeName = getRouteName(route);
                    return mini === false
                      ? routeName
                      : mini === true && hovered === true
                      ? routeName
                      : routeName[0];
                  })()}
                </span>
              </li>
            </div>
          </Link>
        );
      }
    });
  };
  // BRAND
  return <>{createLinks(routes)}</>;
}

export default SidebarLinks;
